import { useEffect, useMemo, useState } from 'react';

import { type MockSetting, type MockSettingName, type QueryMockConfig, getMockSettingName } from './types';

type State = { [K in MockSettingName]?: MockSetting };

class MockSettingStorage {
  private _prefix = 'jwd_page-mock_';

  private _subscribers: ((state: State) => void)[] = [];

  /**
   * Retrieve mock setting from storage
   */
  retrieve(arg: MockSettingName | QueryMockConfig): MockSetting | undefined {
    const _arg = typeof arg === 'object' ? getMockSettingName(arg) : arg;

    const val = window.localStorage.getItem(this._prefix + _arg);

    return val ? JSON.parse(val) : undefined;
  }

  store(name: MockSettingName, value: MockSetting) {
    window.localStorage.setItem(this._prefix + name, JSON.stringify(value));
  }

  persist(state?: State) {
    if (!state) {
      return;
    }

    (Object.keys(state) as (keyof typeof state)[]).forEach((key) => {
      const value = state[key];
      if (value) {
        this.store(key, value);
      }
    });

    this._subscribers.forEach((s) => {
      s({ ...state });
    });
  }

  subscribe(subscriber: (state: State) => void) {
    if (this._subscribers.find((s) => s === subscriber)) {
      return;
    }

    this._subscribers.push(subscriber);

    return () => {
      this._subscribers = this._subscribers.filter((s) => s !== subscriber);
    };
  }

  useStateCopy(mockConfigs?: QueryMockConfig[]) {
    const initialState = useMemo(() => {
      return (mockConfigs ?? []).reduce((acc, mockConf) => {
        const mockSettingName = getMockSettingName(mockConf);
        acc[mockSettingName] = this.retrieve(mockSettingName);
        return acc;
      }, {} as State);
    }, []);

    const [state, setState] = useState<State>(initialState);

    const persist = () => this.persist(state);

    return [state, setState, persist] as const;
  }

  useValue(mockConf?: QueryMockConfig) {
    const mockSettingName = mockConf ? getMockSettingName(mockConf) : undefined;

    const [state, setState] = useState<MockSetting | undefined>(
      mockSettingName ? mockSettingsStorage.retrieve(mockSettingName) : undefined,
    );

    useEffect(() => {
      if (!mockSettingName) {
        return;
      }

      const unsub = this.subscribe((newState) => {
        setState(newState[mockSettingName]);
      });

      return () => {
        unsub?.();
      };
    }, [mockSettingName]);

    return state;
  }
}

export const mockSettingsStorage = new MockSettingStorage();
